import * as Sentry from '@sentry/browser';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';

import {
    PageProgressNode,
    ProgramNode,
    ProgramProgressNode,
    UserInput,
} from 'src/generated/webapp_gql';

import { appClient } from 'src/config/apollo';
import { FinishPageDocument } from 'src/modules/newPrograms/documents/finishPageMutation.generated';
import { FinishProgramDocument } from 'src/modules/newPrograms/documents/finishProgramMutation.generated';
import { GetProgramsDocument } from 'src/modules/newPrograms/documents/getProgramsQuery.generated';
import { StartPageDocument } from 'src/modules/newPrograms/documents/startPageMutation.generated';
import { StartProgramDocument } from 'src/modules/newPrograms/documents/startProgramMutation.generated';
import { selectAnswerOption } from '../effects';

export class ProgramService {
    public apolloClient: ApolloClient<NormalizedCacheObject>;

    constructor(apolloClient: ApolloClient<NormalizedCacheObject> = appClient) {
        this.apolloClient = apolloClient;
    }

    public async fetchPrograms(): Promise<ProgramNode[]> {
        let queryData;

        try {
            queryData = await this.apolloClient.query({
                query: GetProgramsDocument,
                fetchPolicy: 'network-only',
            });
        } catch (error) {
            Sentry.captureException(error);

            return Promise.reject();
        }

        const programs = queryData ? queryData.data.programs : [];

        return programs;
    }

    public async startProgram(programName: string): Promise<ProgramProgressNode[]> {
        return this.apolloClient
            .mutate({
                mutation: StartProgramDocument,
                variables: { programName },
            })
            .then(result => result.data.programProgress)
            .catch(error => {
                Sentry.captureException(error);
            });
    }

    public async finishProgram(programName: string): Promise<ProgramProgressNode[]> {
        return this.apolloClient
            .mutate({
                mutation: FinishProgramDocument,
                variables: { programName },
            })
            .then(result => result.data.finishProgram.programProgress)
            .catch(error => {
                Sentry.captureException(error);
            });
    }

    public async startPage(programName: string, pageName: string): Promise<PageProgressNode[]> {
        return this.apolloClient
            .mutate({
                mutation: StartPageDocument,
                variables: { programName, pageName },
            })
            .then(result => result.data.pageProgress)
            .catch(error => {
                Sentry.captureException(error);
            });
    }

    public selectPageAnswer(
        pageId: string,
        answerIndex: number,
        isSelected?: boolean,
        customOptionValue?: string,
    ): boolean {
        return selectAnswerOption(this.apolloClient, {
            pageId,
            answerIndex,
            isSelected,
            customOptionValue,
        });
    }

    public async finishPage(
        programName: string,
        pageName: string,
        answers: UserInput[],
    ): Promise<PageProgressNode[]> {
        return this.apolloClient
            .mutate({
                mutation: FinishPageDocument,
                variables: {
                    programName,
                    pageName,
                    input: JSON.stringify(answers),
                },
            })
            .then(result => result.data.pageProgress)
            .catch(error => {
                Sentry.captureException(error);
            });
    }
}
